import type { Tour } from '../tour-types';

/**
 * The Statistics tab of an experiment that has run, top to bottom. A draft's Statistics
 * tab shows only a placeholder with none of these anchors, so nothing starts there. The
 * objective panel needs currencies, the traffic split check needs statistics, and the
 * recommendation needs a computed verdict; each step drops out while its panel is missing.
 * Deploy points at the first row that has the button, and is gone once a variant is deployed.
 * Ask (the header's field, on every tab, shown on this tab's tour) is hidden while the results
 * assistant is off, and its step with it.
 */
export const experimentResultsTour: Tour = {
  key: 'experiments.results',
  version: 2,
  routes: ['/experiments/:id'],
  screen: 'statistics',
  steps: [
    {
      anchor: 'results-ask-ai',
      placement: 'bottom',
      since: 2,
      title: 'Ask about this experiment',
      body: 'Ask anything about this experiment: whether the result is significant, how far to trust it, and what to do next. Your team sees the conversation, and each answer uses AI credits.',
    },
    {
      anchor: 'results-objective',
      placement: 'bottom',
      title: 'What is measured',
      body: 'The objective and currency you pick here apply to every table and chart below. Export downloads the data as CSV or the chart as PNG.',
    },
    {
      anchor: 'results-traffic-split',
      placement: 'bottom',
      title: 'Traffic split check',
      body: 'Whether visitors were split the way the weights say. If it turns red, the variants are not comparable and nothing below can be trusted until the cause is found.',
    },
    {
      anchor: 'results-verdict',
      placement: 'bottom',
      title: 'Recommendation',
      body: 'The conclusion so far, and how confident it is. Read it first, then check the evidence under it.',
    },
    {
      anchor: 'results-method',
      placement: 'top',
      title: 'How results are judged',
      body: 'Frequentist gives each variant a p-value against the control; Bayesian gives the chance each one is best. With several variants, the correction keeps false positives in check.',
    },
    {
      anchor: 'results-significance',
      placement: 'top',
      title: 'Significance',
      body: 'Each variant against the control: its lift, the interval around it, and whether the difference is significant. The leading variant is marked in green.',
    },
    {
      anchor: 'results-power',
      placement: 'top',
      title: 'Power analysis',
      body: 'Whether there is enough traffic to detect a real difference, and roughly how long until there is. If the difference is below the floor, running longer will not help.',
    },
    {
      anchor: 'results-statistics',
      placement: 'top',
      title: 'Statistics',
      body: 'The numbers behind the verdict for every variant: its weight, sessions, conversions, average and difference from the control. Hover a column title to see what it means.',
    },
    {
      anchor: 'results-deploy',
      placement: 'left',
      requires: 'experiments:deploy',
      title: 'Deploy a winner',
      body: 'Sends this variant to every visitor and ends the comparison. The numbers collected so far stay as they were.',
    },
    {
      anchor: 'results-secondary',
      placement: 'top',
      title: 'Secondary objectives',
      body: 'How the other events you track moved, with an interval on each lift. They support the result but never decide it, so there is no verdict here.',
    },
    {
      anchor: 'results-charts',
      placement: 'top',
      title: 'Charts over time',
      body: 'The objective per variant, day by day or added up. Use it to spot a day that skews the totals.',
    },
    {
      anchor: 'results-rate-over-time',
      placement: 'top',
      title: 'Rate over time',
      body: 'Whether the result has settled, faded after a novelty bump, or crossed significance and back. Check it before acting on a winner.',
    },
  ],
};
